"use client";

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  Suspense,
} from "react";
import type { ReactNode } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ItemCategory, ItemImage, CategorySelection } from "@/lib/types";
import type { DayOfWeek } from "@/lib/types";
import type { HierarchicalLocationOnChange } from "@/lib/locationIds";
import { sellProfileLocationFromUser } from "@/lib/listing/listingLocationIds";
import type { SellListingFormData } from "@/lib/listing/sellListingFormTypes";
import { createEmptySellListingFormData } from "@/lib/listing/sellListingFormDefaults";
import { emptyFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import {
  getListingSpecFormValidationMessage,
  buildListingAttributesForSubmit,
} from "@/lib/listing/listingSpecPlugin";
import { useActiveCategoryStructure } from "@/lib/hooks/useActiveCategoryStructure";
import { activeSubcategoryIdSet } from "@/lib/activeCategories";
import { useSubcategoryRules } from "@/lib/subcategoryRules";
import Button from "@/components/ui/Button";
import { useToast } from "@/contexts/ToastContext";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { v4 as uuidv4 } from "uuid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import RouteGuard from "@/components/RouteGuard";
import {
  validateSellListingField,
  getSellListingFormErrors,
  sellListingFormIsValid,
  validatePriceAgainstBand,
  type SellListingFormErrors,
} from "@/lib/listing/sellListingValidation";
import {
  useSellPriceBand,
  getCachedSellPriceBand,
  type SellPriceBandSnapshot,
} from "@/lib/hooks/useSellPriceBand";
import { resolveBandTitleForListing } from "@/lib/listing/sellBandTitle";
import { shouldFetchSellPriceBand } from "@/lib/listing/sellPriceBandFetchGate";
import { categoryLabels } from "@/lib/categoryLabels";
import {
  createSellListing,
  formPayloadFromSellForm,
  submitBulkSellListings,
} from "@/lib/listing/sellListingWorkflow";
import { buildSellSuccessHref } from "@/lib/sell/sellSuccessHandoff";
import {
  SELL_FORM_DAYS_OF_WEEK,
  SELL_FORM_HOURS_OF_DAY,
} from "@/lib/listing/sellFormConstants";
import { getCategoryDetectionSettings } from "@/lib/categoryDetectionUtils";
import ProfileCompletionGuard from "@/components/ProfileCompletionGuard";
import StepBasedForm from "@/components/listing/StepBasedForm";
import SubcategoryRulesCard from "@/components/listing/SubcategoryRulesCard";
import BulkListingModeToggle from "@/components/listing/BulkListingModeToggle";
import CommonFieldsSection from "@/components/listing/CommonFieldsSection";
import BulkProgressIndicator from "@/components/listing/BulkProgressIndicator";
import ItemNavigationControls from "@/components/listing/ItemNavigationControls";
import BulkItemFormSections from "@/components/listing/BulkItemFormSections";
import BulkReviewModal from "@/components/listing/BulkReviewModal";
import Modal from "@/components/ui/Modal";
import { useBulkListing } from "@/lib/hooks/useBulkListing";
import {
  validateFullBulkBatch,
  bulkCommonIsValid,
  bulkItemIsComplete,
  getBulkItemSpecError,
  getBulkItemFieldErrors,
  getBulkBatchMinimumItemError,
} from "@/lib/listing/bulkListingValidation";
import { usePricingInsightFlagsContext } from "@/contexts/PricingInsightFlagsContext";
import {
  saveBulkListingDraft,
  loadBulkListingDraft,
  clearBulkListingDraft,
  restoredDraftToState,
  BULK_LISTING_DRAFT_TTL_MS,
} from "@/lib/listing/bulkListingDraft";
import SellPageTermsSummary from "@/components/listing/SellPageTermsSummary";
import {
  parseSellFromRequestSearchParams,
  applySellFromRequestToFormData,
  applySellFromRequestToBulkItem,
} from "@/lib/item-requests";
import {
  BULK_LISTING_MIN_ITEMS,
  BULK_LISTING_MAX_ITEMS,
  BULK_LISTING_MIN_ITEMS_MESSAGE,
  type BulkListingItemDraft,
  type SellListingMode,
} from "@/lib/listing/bulkListingTypes";
import { useOnboarding } from "@/contexts/OnboardingContext";
import posthog from "posthog-js";
import {
  buildListingSubmittedPricingProps,
  captureListingPriceBlockedClient,
} from "@/lib/analytics/sellerPricingFriction";
import {
  buildListingSubmittedFromRequestProps,
  captureSellFromRequestLanded,
} from "@/lib/analytics/captureDemandStrip";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";
import { subscribeSellDevToolboxAdapter } from "@/lib/devToolbox/adapters";
import type { DevImageValidationPreview } from "@/lib/devToolbox/sellerListingMocks";

type FormErrors = SellListingFormErrors;

function SellPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { showToast } = useToast();
  const { userLocation: _userLocation } = useLocation();
  const { user } = useAuth();
  const userId = user?.id;
  const profileLocation = useMemo(
    () => sellProfileLocationFromUser(user),
    [user],
  );
  const { isPromptOpen, completePrompt } = useOnboarding();
  const { activeCategoryStructure } = useActiveCategoryStructure();
  const { getRulesForSubcategory } = useSubcategoryRules();
  const {
    isSellerPriceBandEnforcementEnabled,
    isSellerPriceBandRecommendationChipsEnabled,
  } = usePricingInsightFlagsContext();
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [pickupDays, setPickupDays] = useState<DayOfWeek[]>([]);
  const [uploadedImages, setUploadedImages] = useState<ItemImage[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [_hasDuplicateImages, setHasDuplicateImages] = useState(false);
  const [sellerCategoryDetectionEnabled, setSellerCategoryDetectionEnabled] =
    useState(false); // Default to disabled (user/seller context)

  // Form validation state
  const [errors, setErrors] = useState<FormErrors>({});

  const [currentStep, setCurrentStep] = useState(1);

  const {
    listingMode,
    setListingMode,
    bulkCommon,
    setBulkCommon,
    bulkItems,
    bulkItemIndex,
    setBulkItemIndex,
    currentBulkItem,
    isBulkDirty,
    resetBulkState,
    updateBulkItem,
    onBulkCategorySelect,
    addBulkItem,
    removeBulkItem,
    duplicateBulkItem,
    setBulkSubmitResult,
    setBulkItems,
    resolveBulkSubmitBatchId,
    rememberBulkSubmitBatchId,
    clearBulkSubmitBatchId,
  } = useBulkListing();

  const [modeSwitchDialogOpen, setModeSwitchDialogOpen] = useState(false);
  const [pendingListingMode, setPendingListingMode] =
    useState<SellListingMode | null>(null);
  const [bulkReviewOpen, setBulkReviewOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkSubmitProgress, setBulkSubmitProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [bulkSubmitFinished, setBulkSubmitFinished] = useState(false);
  const [bulkReviewBlockingMessages, setBulkReviewBlockingMessages] = useState<
    string[]
  >([]);
  const [bulkDraftPromptOpen, setBulkDraftPromptOpen] = useState(false);
  const [bulkDraftBanner, setBulkDraftBanner] = useState(false);
  /** Free-text policy messages for current bulk item — blur + review only. */
  const [bulkFreeTextErrors, setBulkFreeTextErrors] = useState<{
    description?: string;
    defects?: string;
  }>({});
  const bulkDraftPromptShownRef = useRef(false);
  /** Sync lock — `isLoading` alone cannot stop double-clicks before re-render */
  const singleSubmitInFlightRef = useRef(false);
  const bulkSubmitInFlightRef = useRef(false);
  const sellIntroOpen = isPromptOpen("sellIntro");
  const [devSellInitialStep, setDevSellInitialStep] = useState(1);
  const [devSellBandErrorOverride, setDevSellBandErrorOverride] = useState<
    string | null
  >(null);
  const [devImageValidationPreview, setDevImageValidationPreview] =
    useState<DevImageValidationPreview>(null);
  const lastDevSellScenarioRef = useRef<DevToolboxScenarioId | null>(null);

  const [formData, setFormData] = useState<SellListingFormData>(
    createEmptySellListingFormData,
  );

  // fromRequest URL prefill — apply during render (not in an effect) so
  // Strict Mode / searchParams identity churn cannot leave the banner on
  // while form fields stay empty (react-hooks/set-state-in-effect).
  const fromRequestPrefill = useMemo(
    () => parseSellFromRequestSearchParams(searchParams),
    [searchParams],
  );
  const fromRequestFlow = searchParams.get("fromRequest") === "true";
  const fromRequestPrefillSignature = fromRequestPrefill
    ? searchParams.toString()
    : "";
  const [appliedSingleFromRequestSig, setAppliedSingleFromRequestSig] =
    useState("");
  const [appliedBulkFromRequestSig, setAppliedBulkFromRequestSig] =
    useState("");
  if (
    listingMode === "single" &&
    fromRequestPrefill &&
    fromRequestPrefillSignature !== appliedSingleFromRequestSig
  ) {
    setAppliedSingleFromRequestSig(fromRequestPrefillSignature);
    setFormData((prev) =>
      applySellFromRequestToFormData(prev, fromRequestPrefill),
    );
  }
  if (
    listingMode === "bulk" &&
    fromRequestPrefill &&
    fromRequestPrefillSignature !== appliedBulkFromRequestSig
  ) {
    setAppliedBulkFromRequestSig(fromRequestPrefillSignature);
    if (fromRequestPrefill.location) {
      setBulkCommon((c) => ({
        ...c,
        location: c.location || fromRequestPrefill.location,
      }));
    }
    setBulkItems((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[0] = applySellFromRequestToBulkItem(next[0], fromRequestPrefill);
      return next;
    });
  }

  const sellFromRequestLandedSigRef = useRef("");
  useEffect(() => {
    if (!fromRequestPrefill || !fromRequestPrefillSignature) return;
    if (sellFromRequestLandedSigRef.current === fromRequestPrefillSignature) {
      return;
    }
    sellFromRequestLandedSigRef.current = fromRequestPrefillSignature;
    captureSellFromRequestLanded({
      itemRequestId: fromRequestPrefill.itemRequestId,
      category: fromRequestPrefill.category || "",
      subcategory: fromRequestPrefill.subcategory,
      listingMode,
    });
  }, [fromRequestPrefill, fromRequestPrefillSignature, listingMode]);

  useEffect(
    () =>
      subscribeSellDevToolboxAdapter({
        lastScenarioRef: lastDevSellScenarioRef,
        setListingMode,
        setFormData,
        setUploadedImages,
        setPickupDays,
        setErrors,
        setDevSellInitialStep,
        setDevSellBandErrorOverride,
        setDevImageValidationPreview,
      }),
    [setListingMode],
  );

  const bandTitle = useMemo(() => {
    const source = listingMode === "single" ? formData : currentBulkItem;
    return resolveBandTitleForListing({
      title: source.title,
      category: source.category,
      subcategory: source.subcategory,
      size: "size" in source ? source.size : undefined,
      brand: "brand" in source ? source.brand : undefined,
      model: "model" in source ? source.model : undefined,
      familySpecs: "familySpecs" in source ? source.familySpecs : undefined,
    });
  }, [listingMode, formData, currentBulkItem]);

  const bandListingAttributes = useMemo(() => {
    const source = listingMode === "single" ? formData : currentBulkItem;
    return buildListingAttributesForSubmit({
      subcategoryId: source.subcategory || "",
      sizeText: "size" in source ? source.size : undefined,
      familySpecs: "familySpecs" in source ? source.familySpecs : undefined,
    });
  }, [listingMode, formData, currentBulkItem]);

  const bandCategory =
    listingMode === "single" ? formData.category : currentBulkItem.category;
  const bandSubcategory =
    listingMode === "single"
      ? formData.subcategory || ""
      : currentBulkItem.subcategory || "";

  const sellPriceBandFetchEnabled = shouldFetchSellPriceBand({
    enforcementEnabled: isSellerPriceBandEnforcementEnabled,
    listingMode,
    currentStep,
    title: bandTitle,
    category: bandCategory,
  });

  const {
    band: sellPriceBand,
    isLoading: sellPriceBandLoading,
    error: sellPriceBandError,
  } = useSellPriceBand({
    enabled: sellPriceBandFetchEnabled,
    title: bandTitle,
    category: bandCategory,
    subcategory: bandSubcategory,
    listingAttributes: bandListingAttributes,
  });

  const resolveBulkItemBand = useCallback(
    (item: typeof currentBulkItem): SellPriceBandSnapshot | null => {
      if (item.id === currentBulkItem.id && sellPriceBand) {
        return sellPriceBand;
      }
      return getCachedSellPriceBand(
        resolveBandTitleForListing({
          title: item.title,
          category: item.category,
          subcategory: item.subcategory,
          size: item.size,
          brand: item.brand,
          model: item.model,
          familySpecs: item.familySpecs,
        }),
        item.category,
        item.subcategory || "",
      );
    },
    [currentBulkItem.id, sellPriceBand],
  );

  const validateField = useCallback(
    (name: string, value: unknown): string | undefined => {
      if (name === "price") {
        const baseError = validateSellListingField("price", value);
        if (baseError) return baseError;
        return validatePriceAgainstBand(String(value ?? ""), sellPriceBand, {
          enforcementActive: isSellerPriceBandEnforcementEnabled,
        });
      }
      if (name === "brand") {
        return validateSellListingField("brand", value, {
          category: formData.category,
        });
      }
      return validateSellListingField(name, value);
    },
    [formData.category, isSellerPriceBandEnforcementEnabled, sellPriceBand],
  );

  /** Re-validates price when band loads or changes (no effect + setState). */
  const displayErrors = useMemo(() => {
    if (!formData.price) {
      return errors;
    }
    const priceError = validateField("price", formData.price);
    if (errors.price === priceError) {
      return errors;
    }
    return { ...errors, price: priceError };
  }, [errors, formData.price, validateField]);

  const validateForm = (): boolean => {
    const newErrors = getSellListingFormErrors(
      {
        title: formData.title,
        price: formData.price,
        category: formData.category,
        location: formData.location,
        brand: formData.brand,
      },
      uploadedImages,
    );
    newErrors.price = validateField("price", formData.price);
    newErrors.description = validateField("description", formData.description);
    newErrors.defects = validateField("defects", formData.defects);
    setErrors(newErrors);
    if (!sellListingFormIsValid(newErrors)) {
      const bandOnlyError = validatePriceAgainstBand(
        formData.price,
        sellPriceBand,
        { enforcementActive: isSellerPriceBandEnforcementEnabled },
      );
      if (bandOnlyError && newErrors.price === bandOnlyError && sellPriceBand) {
        const attempted = Number(formData.price.replace(/,/g, ""));
        if (Number.isFinite(attempted)) {
          captureListingPriceBlockedClient({
            listingMode: "single",
            category: formData.category,
            subcategory: formData.subcategory,
            attemptedPrice: attempted,
            bandMin: sellPriceBand.min,
            bandMax: sellPriceBand.max,
            sampleCount: sellPriceBand.sampleCount,
          });
        }
      }
      return false;
    }
    const specErr = getListingSpecFormValidationMessage({
      subcategoryId: formData.subcategory,
      sizeText: formData.size,
      familySpecs: formData.familySpecs,
      activeSubcategoryIds: activeSubcategoryIdSet(activeCategoryStructure),
    });
    if (specErr) {
      showToast(specErr, "error");
      return false;
    }
    return true;
  };

  const isSingleFormDirty = useCallback(() => {
    return (
      formData.title.trim().length > 0 ||
      formData.price.trim().length > 0 ||
      Boolean(formData.category) ||
      formData.location.trim().length > 0 ||
      uploadedImages.length > 0 ||
      pickupDays.length > 0 ||
      formData.description.trim().length > 0 ||
      formData.defects.trim().length > 0 ||
      formData.brand.trim().length > 0 ||
      formData.size.trim().length > 0 ||
      formData.model.trim().length > 0
    );
  }, [formData, uploadedImages.length, pickupDays.length]);

  const currentBulkFieldErrors = useMemo(() => {
    const all = getBulkItemFieldErrors(currentBulkItem, {
      band: sellPriceBand,
      enforcementActive: isSellerPriceBandEnforcementEnabled,
    });
    return {
      ...all,
      description: bulkFreeTextErrors.description,
      defects: bulkFreeTextErrors.defects,
    };
  }, [
    currentBulkItem,
    sellPriceBand,
    isSellerPriceBandEnforcementEnabled,
    bulkFreeTextErrors,
  ]);

  // Depend on structure (stable when categories unchanged). Build the Set inside
  // memoized consumers so React Compiler / exhaustive-deps stay aligned.
  const currentBulkSpecError = useMemo(
    () =>
      getBulkItemSpecError(
        currentBulkItem,
        activeSubcategoryIdSet(activeCategoryStructure),
      ),
    [currentBulkItem, activeCategoryStructure],
  );

  const bulkCommonLocationError = useMemo(
    () => validateSellListingField("location", bulkCommon.location),
    [bulkCommon.location],
  );

  const bulkCurrentSubcategoryRule =
    getRulesForSubcategory(currentBulkItem.subcategory) ?? null;

  const bulkItemsReadyFlags = useMemo(() => {
    const activeSubcategoryIds = activeSubcategoryIdSet(
      activeCategoryStructure,
    );
    return bulkItems.map((it) =>
      bulkItemIsComplete(
        it,
        getRulesForSubcategory(it.subcategory) ?? null,
        activeSubcategoryIds,
        undefined,
        {
          band: resolveBulkItemBand(it),
          enforcementActive: isSellerPriceBandEnforcementEnabled,
        },
      ),
    );
  }, [
    bulkItems,
    getRulesForSubcategory,
    activeCategoryStructure,
    isSellerPriceBandEnforcementEnabled,
    resolveBulkItemBand,
  ]);

  const bulkReadyCount = useMemo(
    () => bulkItemsReadyFlags.filter(Boolean).length,
    [bulkItemsReadyFlags],
  );

  const requestListingMode = (next: SellListingMode) => {
    if (next === listingMode) return;
    const needConfirm =
      (listingMode === "bulk" && isBulkDirty) ||
      (listingMode === "single" && isSingleFormDirty());
    if (needConfirm) {
      setPendingListingMode(next);
      setModeSwitchDialogOpen(true);
      return;
    }
    applyListingMode(next);
  };

  const applyListingMode = (next: SellListingMode) => {
    setListingMode(next);
    setModeSwitchDialogOpen(false);
    setPendingListingMode(null);
    if (next === "single") {
      resetBulkState();
    }
  };

  const confirmModeSwitch = () => {
    if (pendingListingMode) {
      if (pendingListingMode === "single") {
        resetBulkState();
      }
      setListingMode(pendingListingMode);
    }
    setModeSwitchDialogOpen(false);
    setPendingListingMode(null);
  };

  const cancelModeSwitch = () => {
    setModeSwitchDialogOpen(false);
    setPendingListingMode(null);
  };

  const handleBulkCommonLocationChange: HierarchicalLocationOnChange = (
    value,
    _coordinates,
    _placeId,
    locationIds,
  ) => {
    setBulkCommon((c) => ({
      ...c,
      location: value,
      locationIds: locationIds ?? null,
    }));
  };

  const handleBulkCommonDayToggle = (day: DayOfWeek) => {
    setBulkCommon((c) => {
      const nextDays = c.pickupDays.includes(day)
        ? c.pickupDays.filter((d) => d !== day)
        : [...c.pickupDays, day];
      return { ...c, pickupDays: nextDays };
    });
  };

  const handleBulkItemFieldChange = (name: string, value: unknown) => {
    const id = currentBulkItem.id;
    if (name === "price") {
      const numericValue = String(value).replace(/[^0-9]/g, "");
      if (numericValue === "" || !isNaN(Number(numericValue))) {
        const formattedValue =
          numericValue === ""
            ? ""
            : Number(numericValue).toLocaleString("en-NG");
        updateBulkItem(id, { price: formattedValue });
      }
    } else if (name === "quantity") {
      const numericValue = Math.max(1, parseInt(String(value), 10) || 1);
      updateBulkItem(id, { quantity: numericValue });
    } else {
      updateBulkItem(id, { [name]: value } as Partial<BulkListingItemDraft>);
      if (
        (name === "description" || name === "defects") &&
        bulkFreeTextErrors[name]
      ) {
        setBulkFreeTextErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const handleBulkItemFieldBlur = (name: string, value: unknown) => {
    if (name !== "description" && name !== "defects") return;
    const error = validateSellListingField(name, value);
    setBulkFreeTextErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBulkImagesChange = async (images: ItemImage[]) => {
    updateBulkItem(currentBulkItem.id, { images });
    if (images.length === 3) {
      const hasInvalidFiles = images.some((image) => !image.file);
      if (hasInvalidFiles) {
        showToast(
          "Some images are invalid. Please try uploading them again.",
          "error",
        );
        return;
      }
      const hasLargeFiles = images.some(
        (image) => image.file && image.file.size > 5 * 1024 * 1024,
      );
      if (hasLargeFiles) {
        showToast(
          "Some images are too large. Maximum size is 5MB per image.",
          "error",
        );
        return;
      }
    }
  };

  const buildBulkReviewBlockingMessages = useCallback((): string[] => {
    const activeSubcategoryIds = activeSubcategoryIdSet(
      activeCategoryStructure,
    );
    const { commonErrors, perItemErrors, rulesOk, specErrors } =
      validateFullBulkBatch(
        bulkCommon,
        bulkItems,
        (subId) => getRulesForSubcategory(subId) ?? null,
        activeSubcategoryIds,
      );
    const msgs: string[] = [];
    const minItemsError = getBulkBatchMinimumItemError(bulkItems);
    if (minItemsError) {
      msgs.push(minItemsError);
    }
    if (!bulkCommonIsValid(commonErrors) && commonErrors.location) {
      msgs.push(commonErrors.location);
    }
    bulkItems.forEach((it, i) => {
      const n = i + 1;
      const fe = perItemErrors[i];
      if (fe.title) msgs.push(`Item ${n}: ${fe.title}`);
      if (fe.price) msgs.push(`Item ${n}: ${fe.price}`);
      if (fe.category) msgs.push(`Item ${n}: ${fe.category}`);
      if (fe.images) msgs.push(`Item ${n}: ${fe.images}`);
      if (specErrors[i]) msgs.push(`Item ${n}: ${specErrors[i]}`);
      if (!rulesOk[i]) {
        msgs.push(
          `Item ${n}: Please read and acknowledge the listing rules for this subcategory.`,
        );
      }
    });
    return msgs;
  }, [bulkCommon, bulkItems, getRulesForSubcategory, activeCategoryStructure]);

  const openBulkReview = () => {
    const msgs = buildBulkReviewBlockingMessages();
    if (isSellerPriceBandEnforcementEnabled) {
      for (const item of bulkItems) {
        const band = resolveBulkItemBand(item);
        const bandErr = validatePriceAgainstBand(item.price, band, {
          enforcementActive: true,
        });
        if (!bandErr || !band) continue;
        const attempted = Number(item.price.replace(/,/g, ""));
        if (!Number.isFinite(attempted)) continue;
        captureListingPriceBlockedClient({
          listingMode: "bulk",
          category: item.category,
          subcategory: item.subcategory,
          attemptedPrice: attempted,
          bandMin: band.min,
          bandMax: band.max,
          sampleCount: band.sampleCount,
        });
      }
    }
    setBulkReviewBlockingMessages(msgs);
    setBulkSubmitFinished(false);
    setBulkReviewOpen(true);
  };

  const runBulkSubmit = useCallback(
    async (retryFailedOnly: boolean) => {
      if (bulkSubmitInFlightRef.current) return;
      if (!userId) {
        showToast("Please sign in to sell items", "error");
        return;
      }
      bulkSubmitInFlightRef.current = true;
      setBulkSubmitFinished(false);
      setBulkSubmitting(true);
      const listingBatchId = resolveBulkSubmitBatchId(retryFailedOnly);
      try {
        const result = await submitBulkSellListings({
          userId,
          supabase,
          bulkItems,
          bulkCommon,
          retryFailedOnly,
          listingBatchId,
          activeSubcategoryIds: activeSubcategoryIdSet(activeCategoryStructure),
          getRulesForSubcategory,
          setBulkSubmitResult,
          onProgress: (current, total) => {
            setBulkSubmitProgress({ current, total });
          },
        });
        rememberBulkSubmitBatchId(result.listingBatchId);
        setBulkSubmitting(false);
        setBulkSubmitProgress(null);
        setBulkSubmitFinished(true);
        if (result.allSucceeded) {
          clearBulkSubmitBatchId();
          clearBulkListingDraft(userId);
          posthog.capture("bulk_listing_submitted", {
            item_count: result.targetsCount,
            listing_mode: "bulk",
            ...buildListingSubmittedPricingProps({
              enforcementActive: isSellerPriceBandEnforcementEnabled,
              chipsEnabled: isSellerPriceBandRecommendationChipsEnabled,
              price: bulkItems[0]?.price ?? "",
              band: bulkItems[0] ? resolveBulkItemBand(bulkItems[0]) : null,
            }),
            ...buildListingSubmittedFromRequestProps({
              fromRequest: fromRequestFlow,
              itemRequestId: fromRequestPrefill?.itemRequestId,
            }),
          });
          showToast(
            retryFailedOnly
              ? "All listings submitted successfully."
              : `Successfully created ${result.targetsCount} listing${result.targetsCount === 1 ? "" : "s"}!`,
            "success",
          );
          router.push(buildSellSuccessHref(result.itemIds));
          // Keep lock held through navigation so a second click cannot start another batch.
          return;
        }
        bulkSubmitInFlightRef.current = false;
      } catch (err) {
        setBulkSubmitting(false);
        setBulkSubmitProgress(null);
        setBulkSubmitFinished(false);
        bulkSubmitInFlightRef.current = false;
        showToast(
          err instanceof Error ? err.message : "No items to submit.",
          "error",
        );
      }
    },
    [
      userId,
      bulkItems,
      bulkCommon,
      getRulesForSubcategory,
      activeCategoryStructure,
      isSellerPriceBandEnforcementEnabled,
      isSellerPriceBandRecommendationChipsEnabled,
      resolveBulkItemBand,
      setBulkSubmitResult,
      resolveBulkSubmitBatchId,
      rememberBulkSubmitBatchId,
      clearBulkSubmitBatchId,
      showToast,
      router,
      fromRequestFlow,
      fromRequestPrefill,
    ],
  );

  const bulkFailuresForModal = useMemo(
    () =>
      bulkItems
        .filter((it) => it.submitError)
        .map((it) => ({
          id: it.id,
          title: it.title.trim() || "Untitled",
          message: it.submitError ?? "",
        })),
    [bulkItems],
  );

  const bulkSuccessCount = useMemo(
    () => bulkItems.filter((it) => it.lastSubmitSucceeded).length,
    [bulkItems],
  );

  const dismissSellIntro = useCallback(() => {
    completePrompt("sellIntro");
  }, [completePrompt]);

  useEffect(() => {
    if (!userId || listingMode !== "bulk" || bulkDraftPromptShownRef.current) {
      return;
    }
    const stored = loadBulkListingDraft(userId);
    if (!stored) return;
    const emptyDefault =
      bulkItems.length === 1 &&
      !bulkCommon.location &&
      !bulkItems[0].title &&
      !bulkItems[0].category &&
      bulkItems[0].images.length === 0;
    if (emptyDefault) {
      bulkDraftPromptShownRef.current = true;
      const timer = window.setTimeout(() => {
        setBulkDraftPromptOpen(true);
      }, 0);
      return () => window.clearTimeout(timer);
    }
  }, [userId, listingMode, bulkItems, bulkCommon.location]);

  useEffect(() => {
    if (!userId || listingMode !== "bulk") return;
    const t = window.setTimeout(() => {
      saveBulkListingDraft(userId, bulkCommon, bulkItems);
    }, 800);
    return () => window.clearTimeout(t);
  }, [userId, listingMode, bulkCommon, bulkItems]);

  // Fetch seller category detection setting on mount
  useEffect(() => {
    getCategoryDetectionSettings()
      .then((settings) => {
        setSellerCategoryDetectionEnabled(settings.seller_enabled);
      })
      .catch((error) => {
        console.warn(
          "[SellPage] Error fetching category detection settings:",
          error,
        );
        // Default to disabled on error (user/seller context)
        setSellerCategoryDetectionEnabled(false);
      });
  }, []);

  const _handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name === "price") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "" || !isNaN(Number(numericValue))) {
        const formattedValue =
          numericValue === ""
            ? ""
            : Number(numericValue).toLocaleString("en-NG");
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));

        // Validate price after formatting
        const error = validateField(name, formattedValue);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    } else if (name === "quantity") {
      const numericValue = Math.max(1, parseInt(value, 10) || 1);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Validate the field
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const isListingFreeTextField = (name: string) =>
    name === "title" || name === "description" || name === "defects";

  // Generic field change handler for step-based form
  const handleFieldChange = (name: string, value: unknown) => {
    if (name === "price") {
      const numericValue = String(value).replace(/[^0-9]/g, "");
      if (numericValue === "" || !isNaN(Number(numericValue))) {
        const formattedValue =
          numericValue === ""
            ? ""
            : Number(numericValue).toLocaleString("en-NG");
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
        const error = validateField(name, formattedValue);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    } else if (name === "quantity") {
      const numericValue = Math.max(1, parseInt(String(value), 10) || 1);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else if (isListingFreeTextField(name)) {
      // Free-text policy: blur + submit only (no as-you-type coach).
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name as "title" | "description" | "defects"]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleFieldBlur = (name: string, value: unknown) => {
    if (!isListingFreeTextField(name)) return;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleLocationChange: HierarchicalLocationOnChange = (
    value,
    _coordinates,
    _placeId,
    locationIds,
  ) => {
    setFormData((prev) => ({
      ...prev,
      location: value,
      locationIds: locationIds ?? null,
    }));
    const error = validateField("location", value);
    setErrors((prev) => ({ ...prev, location: error }));
  };

  const handleDayToggle = (day: DayOfWeek) => {
    const newPickupDays = pickupDays.includes(day)
      ? pickupDays.filter((d) => d !== day)
      : [...pickupDays, day];

    setPickupDays(newPickupDays);
    const error = validateField("pickupDays", newPickupDays);
    setErrors((prev) => ({ ...prev, pickupDays: error }));
  };

  const handleImagesChange = async (images: ItemImage[]) => {
    setUploadedImages(images);
    const error = validateField("images", images);
    setErrors((prev) => ({ ...prev, images: error }));

    if (images.length === 3) {
      // Existing validation logic...
      const hasInvalidFiles = images.some((image) => !image.file);
      if (hasInvalidFiles) {
        showToast(
          "Some images are invalid. Please try uploading them again.",
          "error",
        );
        return;
      }

      const hasLargeFiles = images.some(
        (image) => image.file && image.file.size > 5 * 1024 * 1024,
      );
      if (hasLargeFiles) {
        showToast(
          "Some images are too large. Maximum size is 5MB per image.",
          "error",
        );
        return;
      }

      // Check for duplicates...
      if (images.length > 1) {
        // Existing duplicate check logic...
      }

      setHasDuplicateImages(false);
      showToast("All 3 images uploaded successfully!", "success");
    } else {
      setHasDuplicateImages(false);
    }
  };

  const handleCategorySelect = (selection: CategorySelection) => {
    setFormData((prev) => ({
      ...prev,
      category: selection.categoryId,
      subcategory: selection.subcategoryId,
      // Clear brand/size/model when category changes to avoid stale data
      brand: "",
      size: "",
      model: "",
      familySpecs: emptyFamilySpecs(),
    }));
    setErrors((prev) => ({ ...prev, category: "" }));
    setRulesAcknowledged(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!user) {
      showToast("Please sign in to sell items", "error");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (singleSubmitInFlightRef.current) return;
    singleSubmitInFlightRef.current = true;
    setIsLoading(true);
    setError("");

    const imagesError = validateSellListingField("images", uploadedImages);
    if (imagesError) {
      singleSubmitInFlightRef.current = false;
      setIsLoading(false);
      setError(imagesError);
      showToast(imagesError, "error");
      return;
    }

    try {
      const itemId = uuidv4();
      if (!userId) {
        throw new Error("You must be signed in to create a listing.");
      }
      await createSellListing({
        supabase,
        userId,
        itemId,
        formData: formPayloadFromSellForm(formData),
        pickupDays,
        pickupStartHour: formData.pickupStartHour,
        pickupEndHour: formData.pickupEndHour,
        uploadedImages,
        listingBatchId: null,
        listingSource: "single",
        locationIds: formData.locationIds,
      });

      posthog.capture("listing_submitted", {
        category: formData.category,
        subcategory: formData.subcategory || null,
        listing_mode: "single",
        ...buildListingSubmittedPricingProps({
          enforcementActive: isSellerPriceBandEnforcementEnabled,
          chipsEnabled: isSellerPriceBandRecommendationChipsEnabled,
          price: formData.price,
          band: sellPriceBand,
        }),
        ...buildListingSubmittedFromRequestProps({
          fromRequest: fromRequestFlow,
          itemRequestId: fromRequestPrefill?.itemRequestId,
        }),
      });
      showToast("Item successfully created!", "success");
      router.push(buildSellSuccessHref([itemId]));
      // Keep lock held through navigation so a second click cannot create another listing.
    } catch (err) {
      console.error("Error creating item:", err);
      singleSubmitInFlightRef.current = false;
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create item. Please try again.",
      );
      showToast(
        err instanceof Error ? err.message : "Failed to create item",
        "error",
      );
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const renderConfirmationDialog = () => {
    if (!showConfirmation) return null;

    const selectedDays = pickupDays
      .sort((a, b) => a - b)
      .map((day) => SELL_FORM_DAYS_OF_WEEK.find((d) => d.value === day)?.label)
      .join(", ");

    const startHourObj = SELL_FORM_HOURS_OF_DAY.find(
      (h) => h.value === parseInt(formData.pickupStartHour, 10),
    );
    const endHourObj = SELL_FORM_HOURS_OF_DAY.find(
      (h) => h.value === parseInt(formData.pickupEndHour, 10),
    );
    const pickupTime = `${startHourObj?.label} - ${endHourObj?.label}`;
    const subcategoryLabel =
      activeCategoryStructure
        .find((cat) => cat.id === formData.category)
        ?.subcategories.find((sub) => sub.id === formData.subcategory)?.name ||
      "";
    const subcategoryRule =
      getRulesForSubcategory(formData.subcategory) ?? null;

    const detailRow = (label: string, value: ReactNode) => (
      <div className="flex flex-col gap-0.5 first:pt-0 last:pb-0 border-b border-border last:border-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-text-light ">
          {label}
        </dt>
        <dd className="text-sm text-foreground pb-2">{value}</dd>
      </div>
    );

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface rounded-xl shadow-xl max-w-lg w-full flex flex-col max-h-[70vh]">
          {/* Fixed header */}
          <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              Confirm Item Details
            </h2>
            <button
              type="button"
              onClick={() => setShowConfirmation(false)}
              className="p-1 rounded-lg text-text-light hover:text-text-light hover:bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
            {/* Listing rules (when applicable) */}
            {subcategoryRule && subcategoryLabel && (
              <SubcategoryRulesCard
                subcategoryName={subcategoryLabel}
                rule={subcategoryRule}
                acknowledged={!subcategoryRule ? true : rulesAcknowledged}
                onAcknowledgedChange={setRulesAcknowledged}
              />
            )}

            {/* Basic Information */}
            <section className="flex flex-col gap-2 rounded-lg border border-border bg-secondary/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Basic Information
              </h3>
              <dl className="flex flex-col gap-2">
                {detailRow("Title", formData.title)}
                <div className="grid grid-cols-2">
                  {detailRow("Price", `₦${formData.price}`)}
                  {detailRow(
                    "Quantity",
                    `${formData.quantity} ${formData.quantity > 1 ? "units" : "unit"}`,
                  )}
                </div>
                <div className="grid grid-cols-2">
                  {detailRow(
                    "Category",
                    categoryLabels[formData.category as ItemCategory] ||
                      formData.category,
                  )}
                  {detailRow("Subcategory", subcategoryLabel)}
                </div>
                {formData.brand && detailRow("Brand", formData.brand)}
                {formData.size && detailRow("Size", formData.size)}
                {formData.model && detailRow("Model", formData.model)}
                {detailRow("Location", formData.location)}
                {detailRow("Defects", formData.defects || "—")}
              </dl>
            </section>

            {/* Description */}
            <section className="rounded-lg border border-border bg-secondary/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Description
              </h3>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {formData.description || "—"}
              </p>
            </section>

            {/* Pickup Details */}
            <section className="rounded-lg border border-border bg-secondary/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Pickup Details
              </h3>
              <dl className="flex flex-col gap-2">
                {detailRow(
                  "Available days",
                  selectedDays.length > 0 ? selectedDays : "—",
                )}
                {detailRow("Time window", pickupTime)}
              </dl>
            </section>

            {/* Images */}
            <section className="rounded-lg border border-border bg-secondary/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-1">
                Images
              </h3>
              <p className="text-sm text-text-light">
                {uploadedImages.length} image
                {uploadedImages.length !== 1 ? "s" : ""} uploaded
              </p>
            </section>
          </div>

          {/* Fixed footer */}
          <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-border bg-secondary/30">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={isLoading || (!!subcategoryRule && !rulesAcknowledged)}
              haptic="light"
            >
              {isLoading ? "Creating..." : "Create Item"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <Modal
        isOpen={sellIntroOpen}
        onClose={dismissSellIntro}
        title="Before you list"
        maxWidth="md"
        hideFooter
      >
        <div className="space-y-3 text-sm text-text-light">
          <p>
            Every listing is reviewed for quality and safety. Approvals
            typically happen within a few hours (it can take longer during busy
            periods).
          </p>
          <p>
            Use <strong className="text-foreground">three clear photos</strong>,
            an honest description, a fair price, and the right category so
            buyers can find your item.
          </p>
          <p>
            Selling several things from the same place? Choose{" "}
            <strong className="text-foreground">
              Several items ({BULK_LISTING_MIN_ITEMS}–{BULK_LISTING_MAX_ITEMS})
            </strong>{" "}
            at the top — enter location and pickup once, then list each item in
            one batch.
          </p>
          <div className="flex flex-wrap justify-end gap-2 pt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={dismissSellIntro}
            >
              Skip
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={dismissSellIntro}
              haptic="light"
            >
              Got it
            </Button>
          </div>
        </div>
      </Modal>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">
          {listingMode === "single"
            ? "Sell your pre-owned household item"
            : "List multiple items"}
        </h1>
        <p className="mb-6 text-text-light">
          {listingMode === "single"
            ? "List your household items for sale quickly and easily. All listings will be reviewed before appearing in the marketplace."
            : "Enter location and pickup once, then complete each listing. Review everything together when you are ready — up to 10 items per batch."}
        </p>

        {/* Request Prefill Banner */}
        {searchParams.get("fromRequest") === "true" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  This form has been prefilled from an item request
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    This means that someone is currently looking for this item.
                    The form has been prefilled with some details from their
                    request.{" "}
                    <span className="font-medium">
                      Adjust the information as needed before submitting.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <BulkListingModeToggle
          mode={listingMode}
          onChange={requestListingMode}
          disabled={fromRequestFlow}
        />
        {listingMode === "single" && !fromRequestFlow ? (
          <p className="text-sm text-text-light mt-4 mb-4">
            Have more than one item to sell? Switch to{" "}
            <strong className="font-medium text-foreground">
              Several items ({BULK_LISTING_MIN_ITEMS}–{BULK_LISTING_MAX_ITEMS})
            </strong>{" "}
            above — share location and pickup once for the whole batch.
          </p>
        ) : null}
        {fromRequestFlow ? (
          <p className="text-sm text-text-light mb-4">
            Multiple-item mode is unavailable when listing from a buyer request.
          </p>
        ) : null}

        {listingMode === "bulk" && !fromRequestFlow ? (
          <div className="rounded-xl border border-primary-100 bg-primary-50/70 px-4 py-3 text-sm text-primary-900 mb-4">
            <p className="font-medium text-primary-900">How it works</p>
            <ol className="mt-2 list-decimal list-inside space-y-1 text-primary-900/90">
              <li>
                Add at least {BULK_LISTING_MIN_ITEMS} items (use single-item
                mode for one listing).
              </li>
              <li>Set shared location and pickup windows below.</li>
              <li>
                Use Next and Previous to edit each listing (photos are per
                item).
              </li>
              <li>
                Open review when everything looks good — we create your listings
                one after another.
              </li>
            </ol>
          </div>
        ) : null}

        {listingMode === "bulk" &&
        !fromRequestFlow &&
        bulkItems.length < BULK_LISTING_MIN_ITEMS ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-6"
            role="status"
          >
            {BULK_LISTING_MIN_ITEMS_MESSAGE}
          </div>
        ) : null}

        {listingMode === "single" ? (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-surface p-6 rounded-xl shadow-sm border border-border">
              <StepBasedForm
                formData={formData}
                errors={displayErrors}
                uploadedImages={uploadedImages}
                pickupDays={pickupDays}
                onFormDataChange={(data) => {
                  setFormData((prev) => ({
                    ...prev,
                    ...data,
                    pickupAvailability: prev.pickupAvailability,
                  }));
                }}
                onErrorsChange={(errs) => setErrors(errs)}
                onImagesChange={handleImagesChange}
                onPickupDaysChange={setPickupDays}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
                onLocationChange={handleLocationChange}
                onCategorySelect={handleCategorySelect}
                onDayToggle={handleDayToggle}
                validateField={validateField}
                onSubmit={() => {
                  const fakeEvent = {
                    preventDefault: () => {},
                  } as React.FormEvent<HTMLFormElement>;
                  handleSubmit(fakeEvent);
                }}
                isLoading={isLoading}
                sellerCategoryDetectionEnabled={sellerCategoryDetectionEnabled}
                profileLocation={profileLocation}
                onStepChange={setCurrentStep}
                categoryStructure={activeCategoryStructure}
                subcategoryRule={
                  getRulesForSubcategory(formData.subcategory) ?? null
                }
                rulesAcknowledged={rulesAcknowledged}
                onRulesAcknowledgedChange={setRulesAcknowledged}
                priceBandEnforcementActive={isSellerPriceBandEnforcementEnabled}
                sellPriceBand={sellPriceBand}
                sellPriceBandLoading={sellPriceBandLoading}
                sellPriceBandError={
                  devSellBandErrorOverride ?? sellPriceBandError
                }
                bandTitle={bandTitle}
                listingAttributes={bandListingAttributes}
                bandRecommendationChipsEnabled={
                  isSellerPriceBandRecommendationChipsEnabled
                }
                initialStep={devSellInitialStep}
                devImageValidationPreview={devImageValidationPreview}
              />
            </div>

            <SellPageTermsSummary />
          </form>
        ) : (
          <div className="space-y-8 pb-24 sm:pb-8">
            {bulkDraftBanner ? (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                role="status"
              >
                Draft restored. Photos are not saved in browser drafts — upload
                3 images per item again. Drafts expire after{" "}
                {Math.round(BULK_LISTING_DRAFT_TTL_MS / 86400000)} days.
              </div>
            ) : null}

            <CommonFieldsSection
              location={bulkCommon.location}
              locationError={bulkCommonLocationError}
              profileLocation={profileLocation}
              onLocationChange={handleBulkCommonLocationChange}
              pickupDays={bulkCommon.pickupDays}
              pickupStartHour={bulkCommon.pickupStartHour}
              pickupEndHour={bulkCommon.pickupEndHour}
              onDayToggle={handleBulkCommonDayToggle}
              onPickupStartHourChange={(v) =>
                setBulkCommon((c) => ({ ...c, pickupStartHour: v }))
              }
              onPickupEndHourChange={(v) =>
                setBulkCommon((c) => ({ ...c, pickupEndHour: v }))
              }
            />

            <div className="bg-surface p-6 rounded-xl shadow-sm border border-border">
              <div className="mb-6 pb-4 border-b border-border">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-light mb-1">
                  Step 2 · This listing
                </p>
                <p className="text-sm text-text-light">
                  Details and photos apply only to this item. Switch items above
                  when you are done.
                </p>
              </div>
              <BulkItemFormSections
                draft={currentBulkItem}
                categoryStructure={activeCategoryStructure}
                subcategoryRule={bulkCurrentSubcategoryRule}
                errors={currentBulkFieldErrors}
                specError={currentBulkSpecError}
                sellerCategoryDetectionEnabled={sellerCategoryDetectionEnabled}
                bandTitle={bandTitle}
                listingAttributes={bandListingAttributes}
                priceBandEnforcementActive={isSellerPriceBandEnforcementEnabled}
                sellPriceBand={sellPriceBand}
                sellPriceBandLoading={sellPriceBandLoading}
                sellPriceBandError={sellPriceBandError}
                bandRecommendationChipsEnabled={
                  isSellerPriceBandRecommendationChipsEnabled
                }
                onCategorySelect={(sel) =>
                  onBulkCategorySelect(currentBulkItem.id, sel)
                }
                onFieldChange={handleBulkItemFieldChange}
                onFieldBlur={handleBulkItemFieldBlur}
                onImagesChange={handleBulkImagesChange}
                onRulesAcknowledgedChange={(v) =>
                  updateBulkItem(currentBulkItem.id, {
                    rulesAcknowledged: v,
                  })
                }
              />
            </div>

            <BulkProgressIndicator
              currentIndex={bulkItemIndex}
              totalItems={bulkItems.length}
              readyCount={bulkReadyCount}
            />

            <ItemNavigationControls
              currentIndex={bulkItemIndex}
              totalItems={bulkItems.length}
              onPrevious={() => {
                setBulkFreeTextErrors({});
                setBulkItemIndex((i) => Math.max(0, i - 1));
              }}
              onNext={() => {
                setBulkFreeTextErrors({});
                setBulkItemIndex((i) => Math.min(bulkItems.length - 1, i + 1));
              }}
              onAddItem={addBulkItem}
              onRemoveItem={() => removeBulkItem(currentBulkItem.id)}
              onDuplicateItem={() => duplicateBulkItem(currentBulkItem.id)}
              disableRemove={bulkItems.length <= BULK_LISTING_MIN_ITEMS}
            />

            <SellPageTermsSummary />

            <div className="">
              <Button
                type="button"
                variant="primary"
                className="shadow-md ring-2 ring-primary/15"
                onClick={openBulkReview}
                haptic="light"
              >
                Review &amp; submit batch
              </Button>
            </div>

            <div
              className="sm:hidden fixed bottom-0 left-0 right-0 z-40 sio-glass-bar px-4 pt-3"
              style={{
                paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))",
              }}
            >
              <Button
                type="button"
                variant="primary"
                className="w-full shadow-lg ring-2 ring-primary/20"
                onClick={openBulkReview}
                haptic="light"
              >
                Review &amp; submit batch
              </Button>
            </div>
          </div>
        )}

        {showConfirmation &&
          listingMode === "single" &&
          renderConfirmationDialog()}

        <Modal
          isOpen={modeSwitchDialogOpen}
          onClose={cancelModeSwitch}
          title="Switch listing mode?"
          hideFooter
          maxWidth="md"
        >
          <p className="text-sm text-text-light">
            {pendingListingMode === "bulk"
              ? "You will start a multiple-item batch (at least 2 items). If you switch back to single-item mode without confirming here, your single-item draft is kept."
              : "Leaving multiple-item mode clears your batch, shared location, and pickup settings for this session."}
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={cancelModeSwitch}
            >
              Cancel
            </Button>
            <Button type="button" onClick={confirmModeSwitch} haptic="light">
              Continue
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={bulkDraftPromptOpen}
          onClose={() => setBulkDraftPromptOpen(false)}
          title="Resume saved draft?"
          hideFooter
          maxWidth="md"
        >
          <p className="text-sm text-text-light">
            We found a multiple-item draft from the last 7 days. Photos are not
            included — you will need to upload images again for each item.
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (userId) clearBulkListingDraft(userId);
                setBulkDraftPromptOpen(false);
              }}
            >
              Start fresh
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!userId) return;
                const stored = loadBulkListingDraft(userId);
                if (!stored) {
                  setBulkDraftPromptOpen(false);
                  return;
                }
                const restored = restoredDraftToState(stored);
                setBulkCommon(restored.common);
                setBulkItems(restored.items);
                setBulkItemIndex(0);
                setBulkDraftPromptOpen(false);
                setBulkDraftBanner(true);
              }}
              haptic="light"
            >
              Restore draft
            </Button>
          </div>
        </Modal>

        <BulkReviewModal
          isOpen={bulkReviewOpen}
          onClose={() => {
            if (!bulkSubmitting) setBulkReviewOpen(false);
          }}
          common={bulkCommon}
          items={bulkItems}
          categoryStructure={activeCategoryStructure}
          canSubmit={bulkReviewBlockingMessages.length === 0}
          blockingMessages={bulkReviewBlockingMessages}
          onJumpToItem={(index) => {
            const item = bulkItems[index];
            if (item) {
              const all = getBulkItemFieldErrors(item, {
                band: sellPriceBand,
                enforcementActive: isSellerPriceBandEnforcementEnabled,
              });
              setBulkFreeTextErrors({
                description: all.description,
                defects: all.defects,
              });
            } else {
              setBulkFreeTextErrors({});
            }
            setBulkItemIndex(index);
          }}
          onConfirmSubmit={() => runBulkSubmit(false)}
          isSubmitting={bulkSubmitting}
          submitProgress={bulkSubmitProgress}
          submitFinished={bulkSubmitFinished}
          successCount={bulkSuccessCount}
          failures={bulkFailuresForModal}
          onRetryFailed={() => {
            setBulkSubmitFinished(false);
            void runBulkSubmit(true);
          }}
          itemsReady={bulkItemsReadyFlags}
        />
      </div>
    </div>
  );
}

export default function SellPage() {
  return (
    <RouteGuard>
      <ProfileCompletionGuard>
        <Suspense fallback={<div>Loading...</div>}>
          <SellPageContent />
        </Suspense>
      </ProfileCompletionGuard>
    </RouteGuard>
  );
}
